import type { FormHTMLAttributes, HTMLAttributes, JSX, ReactNode } from "react";

import { cn } from "../../lib/cn.js";

import {
  getFormActionsClassName,
  getFormGridClassName,
  getFormRootClassName,
  type FormActionsVariant,
  type FormGridColumns,
  type FormRootSize,
} from "./FormLayout.helpers.js";

export interface FormShellProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  size?: FormRootSize;
}

export function FormShell({
  children,
  className,
  size = "lg",
  ...props
}: FormShellProps): JSX.Element {
  return (
    <div className={getFormRootClassName(size, className)} {...props}>
      {children}
    </div>
  );
}

export interface FormRootProps extends FormHTMLAttributes<HTMLFormElement> {
  children: ReactNode;
  size?: FormRootSize;
}

export function FormRoot({
  children,
  className,
  size = "lg",
  ...props
}: FormRootProps): JSX.Element {
  return (
    <form className={getFormRootClassName(size, className)} {...props}>
      {children}
    </form>
  );
}

export interface FormPanelProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function FormPanel({ children, className, ...props }: FormPanelProps): JSX.Element {
  return (
    <div
      className={cn("overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-border/70", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export interface FormStackProps {
  children: ReactNode;
  className?: string;
}

export function FormStack({ children, className }: FormStackProps): JSX.Element {
  return <div className={cn("grid gap-0", className)}>{children}</div>;
}

export interface FormSectionProps {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
}

export function FormSection({
  title,
  description,
  children,
  actions,
  className,
  contentClassName,
}: FormSectionProps): JSX.Element {
  const hasHeader = Boolean(title || description || actions);

  return (
    <section className={cn("border-b border-border/60 px-5 py-5 last:border-b-0", className)}>
      {hasHeader ? (
        <div className="grid gap-4 md:grid-cols-[minmax(9rem,12rem)_1fr] md:gap-6">
          <div className="min-w-0 space-y-1">
            {title ? <h2 className="text-sm font-semibold text-foreground">{title}</h2> : null}
            {description ? (
              <p className="max-w-48 text-xs leading-5 text-muted-foreground">{description}</p>
            ) : null}
            {actions ? (
              <div className="flex flex-wrap items-center gap-2 pt-2">{actions}</div>
            ) : null}
          </div>
          <div className={cn("grid gap-4", contentClassName)}>{children}</div>
        </div>
      ) : null}
      {!hasHeader ? <div className={cn("grid gap-4", contentClassName)}>{children}</div> : null}
    </section>
  );
}

export interface FormGridProps {
  children: ReactNode;
  columns?: FormGridColumns;
  className?: string;
}

export function FormGrid({ children, columns = 1, className }: FormGridProps): JSX.Element {
  return <div className={getFormGridClassName(columns, className)}>{children}</div>;
}

export interface FieldStackProps {
  children: ReactNode;
  className?: string;
}

export function FieldStack({ children, className }: FieldStackProps): JSX.Element {
  return <div className={cn("grid gap-2", className)}>{children}</div>;
}

export interface FormActionsProps {
  children: ReactNode;
  variant?: FormActionsVariant;
  className?: string;
}

export function FormActions({
  children,
  className,
  variant = "inline",
}: FormActionsProps): JSX.Element {
  return <div className={getFormActionsClassName(variant, className)}>{children}</div>;
}
